import {Helmet} from '@dr.pogodin/react-helmet';
import {isFeatureEnabled} from 'feature-flags';
import {Show} from 'platform/foundation';

import {isNotNil} from 'ramda';

import {useGetCommentsQuery, useGetNoteQuery, useGetParticipationQuery} from '@dms/api/core';
import {EntityResourceIds} from '@dms/api/shared';
import {featureFlags} from '@dms/feature-flags';
import {TaskGridPage, useTasksCount} from '@dms/features/tasks';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {
  Checklists,
  DetailTemplate,
  EMPTY_PLACEHOLDER,
  Section,
  SectionItem,
  getCustomerName,
  interestSearchParams,
  useChecklist,
  usePermissions,
} from '@dms/shared';

import {buildArray, useRequiredParams} from 'shared/web';

import {ActivityLog} from './(sections)/ActivityLog/ActivityLog';
import {Buying} from './(sections)/Buying/Buying';
import {Customer} from './(sections)/Customer/Customer';
import {Overview} from './(sections)/Overview/Overview';
import {Selling} from './(sections)/Selling/Selling';
import {SellingV2} from './(sections)/Selling/SellingV2';
import {InterestActivityPanel} from './components/InterestActivityPanel/InterestActivityPanel';
import {useInterestActions} from './hooks/useInterestActions';
import {useInterestData} from './hooks/useInterestData';
import {useInterestDropdownFlags} from './hooks/useInterestDropdownFlags';
import {useInterestFlags} from './hooks/useInterestFlags';
import {useInterestParameters} from './hooks/useInterestParameters';
import {useInterestSnippets} from './hooks/useInterestSnippets';

export function InterestDetail() {
  const {id: interestId} = useRequiredParams();

  const {interest, customer, isLoading, isError} = useInterestData(interestId);

  const snippets = useInterestSnippets(interestId);
  const flags = useInterestFlags(interestId);
  const dropdownFlags = useInterestDropdownFlags(interestId);
  const actions = useInterestActions(interestId);
  const {data: note} = useGetNoteQuery({
    resourceId: EntityResourceIds.interest,
    recordId: interestId,
    context: EntityResourceIds.interest,
  });
  const {data: comments = []} = useGetCommentsQuery({
    resourceId: EntityResourceIds.interest,
    recordId: interestId,
  });
  const parameters = useInterestParameters(interestId, comments.length > 0, note?.text);

  const {data: interestParticipation} = useGetParticipationQuery({
    recordId: interestId,
    resourceId: EntityResourceIds.interest,
  });

  const {badgeSettings, checklistSettings} = useChecklist({
    recordId: interestId,
    resourceId: 'INTEREST',
  });

  const {count: tasksTodoCount} = useTasksCount({
    entityId: interestId,
    relation: EntityResourceIds.interest,
    state: 'TODO',
  });
  const {count: tasksInProgressCount} = useTasksCount({
    entityId: interestId,
    relation: EntityResourceIds.interest,
    state: 'IN_PROGRESS',
  });
  const tasksCountBadge = tasksTodoCount + tasksInProgressCount;

  const [
    canReadInterestTag,
    hasAddInterestTagPermission,
    hasUpdateInterestTagPermission,
    hasAddInterestTagToRecordPermission,
    hasRemoveInterestTagFromRecordPermission,
    hasRemoveInterestTagPermission,
    hasClearInterestTagExpirationPermission,
    hasSetInterestTagExpirationPermission,
  ] = usePermissions({
    permissionKeys: [
      'interestTagRead',
      'interestTagAdd',
      'interestTagUpdate',
      'interestTagAddToRecord',
      'interestTagRemoveFromRecord',
      'interestTagRemove',
      'interestTagClearExpiration',
      'interestTagSetExpiration',
    ],
    scopes: {
      interestTagAddToRecord: {
        participation: interestParticipation,
        branchId: interest?.branchId,
      },
      interestTagRemoveFromRecord: {
        participation: interestParticipation,
        branchId: interest?.branchId,
      },
      interestTagRead: {
        participation: interestParticipation,
        branchId: interest?.branchId,
      },
    },
  });

  const canCreateTag = hasAddInterestTagPermission && hasUpdateInterestTagPermission;

  const canAssignOrUnassignTag =
    hasAddInterestTagToRecordPermission && hasRemoveInterestTagFromRecordPermission;

  const canDeleteTag =
    hasRemoveInterestTagPermission &&
    hasClearInterestTagExpirationPermission &&
    hasSetInterestTagExpirationPermission;

  const sections = buildArray<SectionItem>()
    .add({
      id: interestSearchParams.interestDetail.overview,
      label: i18n.t('entity.interest.labels.overviewTab'),
      'data-testid': testIds.interest.detail('sectionItem-overview'),
      content: <Overview interestId={interestId} />,
    })
    .add({
      id: interestSearchParams.interestDetail.customer,
      label: i18n.t('entity.interest.labels.customerTab'),
      'data-testid': testIds.interest.detail('sectionItem-customer'),
      content: <Customer interestId={interestId} />,
    })
    .whenFeatureEnabled(featureFlags.SALES_TEST_DRIVES_V2, {
      id: interestSearchParams.interestDetail.selling,
      label: i18n.t('entity.interest.labels.sellingTab'),
      'data-testid': testIds.interest.detail('sectionItem-selling'),
      content: <SellingV2 interestId={interestId} />,
    })
    .whenFeatureNotEnabled(featureFlags.SALES_TEST_DRIVES_V2, {
      id: interestSearchParams.interestDetail.selling,
      label: i18n.t('entity.interest.labels.sellingTab'),
      'data-testid': testIds.interest.detail('sectionItem-selling'),
      content: <Selling interestId={interestId} />,
    })
    .add({
      id: interestSearchParams.interestDetail.buying,
      label: i18n.t('entity.interest.labels.buyingTab'),
      'data-testid': testIds.interest.detail('sectionItem-buying'),
      content: <Buying interestId={interestId} />,
      hasSeparator:
        isFeatureEnabled(featureFlags.CORE_ACTIVITY_LOGS) ||
        isFeatureEnabled(featureFlags.SALE_TASKS_CHECKLIST) ||
        isFeatureEnabled(featureFlags.CORE_TASK_MANAGEMENT),
    })
    .whenFeatureEnabled(featureFlags.SALE_TASKS_CHECKLIST, {
      id: interestSearchParams.interestDetail.checklists,
      label: i18n.t('entity.checklist.labels.title'),
      content: (
        <Section>
          <Checklists {...checklistSettings} />
        </Section>
      ),
      badge: badgeSettings,
      hasSeparator: isFeatureEnabled(featureFlags.CORE_TASK_MANAGEMENT),
      'data-testid': testIds.interest.detail('sectionItem-checklists'),
    })
    .whenFeatureEnabled(featureFlags.CORE_TASK_MANAGEMENT, {
      id: interestSearchParams.interestDetail.tasks,
      label: i18n.t('entity.task.labels.tasks'),
      'data-testid': testIds.interest.detail('sectionItem-tasks'),
      content: (
        <TaskGridPage
          gridCode="interest-task"
          entityId={interestId}
          data-testid={testIds.interest.detail('tasks')}
        />
      ),
      badge: {
        colorScheme: 'neutral',
        value: tasksCountBadge,
      },
      hasSeparator: isFeatureEnabled(featureFlags.CORE_ACTIVITY_LOGS),
    })
    .whenFeatureEnabled(featureFlags.CORE_ACTIVITY_LOGS, {
      id: interestSearchParams.interestDetail.actionLogs,
      label: i18n.t('general.labels.activityLogs'),
      'data-testid': testIds.interest.detail('sectionItem-activityLogs'),
      content: <ActivityLog />,
    });

  return (
    <>
      <Show when={isNotNil(customer)}>
        <Helmet title={i18n.t('page.interest.title', {name: getCustomerName(customer)})} />
      </Show>
      <DetailTemplate
        key={interest?.id}
        isLoading={isLoading}
        isError={isError}
        activityPanel={<InterestActivityPanel interestId={interestId} />}
        header={{
          title: getCustomerName(customer) ?? EMPTY_PLACEHOLDER,
          icon: 'content/next_week',
          height: 61,
          recordId: interestId,
          resourceId: EntityResourceIds.interest,
          controls: ['ASSIGNEE', 'SOURCE', 'INTEREST_BRANCH'],
          enableTeams: isFeatureEnabled(featureFlags.CORE_TEAMS),
          flags,
          dropdownFlags,
          parameters,
          snippets,
          actions,
        }}
        sections={sections}
        data-testid={testIds.interest.detail('header')}
        permissions={{
          tag: {
            isDeletable: canDeleteTag,
            isUpdatable: canCreateTag,
            isReadable: canReadInterestTag,
            isCreatable: canCreateTag,
            isAssignable: canAssignOrUnassignTag,
            isUnassignable: canAssignOrUnassignTag,
          },
        }}
      />
    </>
  );
}
